import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import RawHeader from "../components/RawHeader";
import AdminNav from "../components/AdminNav";
import { api } from "../lib/api";

const fmt = (n) => `£${Number(n || 0).toFixed(2)}`;
const ago = (iso) => {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
};

const AdminAbandoned = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/abandoned-carts");
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await api.post("/admin/abandoned-carts/send-now");
      const n = r?.data?.sent ?? 0;
      toast.success(`${n} reminder${n === 1 ? "" : "s"} dispatched`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cron failed");
    } finally {
      setSending(false);
    }
  };

  const totals = rows.reduce(
    (acc, r) => {
      acc.total += 1;
      if (r.recovered || r.completed) acc.recovered += 1;
      if (r.reminder_sent) acc.sent += 1;
      acc.potential += Number(r.subtotal_gbp || 0);
      return acc;
    },
    { total: 0, recovered: 0, sent: 0, potential: 0 }
  );

  return (
    <div className="min-h-screen text-white" data-testid="admin-abandoned-page">
      <RawHeader variant="admin" />
      <section className="max-w-[1600px] mx-auto px-6 lg:px-10 py-10">
        <AdminNav />
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] mb-2">// RECOVERY</div>
            <h1 className="font-display text-4xl sm:text-5xl text-[#d4af37]">Abandoned Carts</h1>
          </div>
          <button
            data-testid="abandoned-send-now-btn"
            onClick={sendNow}
            disabled={sending}
            className="btn-raw btn-raw-primary"
          >
            {sending ? "DISPATCHING…" : "✉ SEND REMINDERS NOW"}
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <Stat label="BASKETS SAVED" value={totals.total} />
          <Stat label="REMINDERS SENT" value={totals.sent} />
          <Stat label="RECOVERED" value={totals.recovered} />
          <Stat label="POTENTIAL VALUE" value={fmt(totals.potential)} />
        </div>

        {loading ? (
          <div className="text-center py-16 text-zinc-600 text-xs tracking-[0.3em] uppercase">LOADING…</div>
        ) : rows.length === 0 ? (
          <div className="border border-dashed border-zinc-800 p-12 text-center text-zinc-500 text-xs tracking-[0.3em] uppercase">
            NO BASKETS CAPTURED YET
          </div>
        ) : (
          <div className="raw-panel divide-y divide-zinc-800 overflow-hidden" data-testid="abandoned-list">
            {rows.map((r) => (
              <div key={r.id} className="p-4 grid grid-cols-1 md:grid-cols-6 gap-3 items-center" data-testid={`abandoned-row-${r.id}`}>
                <div className="md:col-span-2 min-w-0">
                  <div className="font-mono text-sm text-white truncate">{r.email}</div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-1">{ago(r.created_at)}</div>
                </div>
                <div className="text-xs text-zinc-400 md:col-span-2">
                  {(r.items || []).map((it, i) => (
                    <div key={i} className="truncate">
                      {it.product_name || it.product_id} <span className="text-zinc-600">× {it.quantity} / {it.size}</span>
                    </div>
                  ))}
                </div>
                <div className="font-mono text-[#d4af37]">{fmt(r.subtotal_gbp)}</div>
                <div className="flex gap-2 flex-wrap">
                  {r.completed && (
                    <span className="text-[9px] tracking-[0.3em] uppercase px-2 py-1 border border-[#d4af37] text-[#d4af37]">PAID</span>
                  )}
                  {r.reminder_sent && !r.completed && (
                    <span className="text-[9px] tracking-[0.3em] uppercase px-2 py-1 border border-zinc-600 text-zinc-300">SENT</span>
                  )}
                  {!r.reminder_sent && !r.completed && (
                    <span className="text-[9px] tracking-[0.3em] uppercase px-2 py-1 border border-zinc-700 text-zinc-500">PENDING</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="text-xs text-zinc-500 mt-6">
          Reminders auto-fire every 30 minutes for carts aged 1h–24h. One reminder per cart — no spam.
        </p>
      </section>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="raw-panel p-4">
    <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 mb-1">{label}</div>
    <div className="font-display text-2xl text-[#d4af37]">{value}</div>
  </div>
);

export default AdminAbandoned;
